import { useMutation } from '@apollo/client'
import { Formik } from 'formik'
import { ReactElement } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { Button } from 'components/v2/Button'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import FormLayout from 'components/v2/FormLayout'
import { INewPartnerStep2 } from 'features/Partners/types'
import {
  ADD_PARTNER_BANK_DATA,
  ADD_PARTNER_CONTACTS,
  DELETE_PARTNER_CONTACTS,
  CHANGE_PARTNER_CONTACTS
} from 'features/Partners/mutations-legacy'
import { getUnprocessableErrors } from 'utils/error'
import { PartnerDataContactsCard } from 'features/Partners/components/PartnerContactsCard'
import { schema } from './schema'
import { PartnerBankDataCard } from 'features/Partners/components/PartnerBankDataCard'
import { useParams, useSearchParams } from 'react-router-dom'
import { getDifference, getEntitiesDifference } from 'utils/object'
import { isEmpty } from 'lodash'
import {
  EMPTY_PARTNER_CONTACT,
  getNewBankData,
  getNewContacts,
  mapPartnerBankDataToMutation,
  mapPartnerContactsToMutation
} from 'features/Partners/utils'
import { GTChangePartnerContactInput } from 'gql/types.generated'

export const NewPartnerStep2 = ({
  onSubmit,
  onCancel,
  partner
}: {
  partner?: INewPartnerStep2
  isEdit?: boolean
  onSubmit: () => void
  onCancel: () => void
}): ReactElement => {
  const intl = useIntl()
  const [searchParams] = useSearchParams()

  const partnerId = useParams().partnerId || searchParams.get('partnerId') || ''

  const [addPartnerContacts, { loading: addPartnerContactsLoading, error: addPartnerContactsError }] =
    useMutation(ADD_PARTNER_CONTACTS)

  const [deletePartnerContacts, { loading: deletePartnerContactsLoading, error: deletePartnerContactsError }] =
    useMutation(DELETE_PARTNER_CONTACTS)

  const [changePartnerBankData, { loading: changePartnerBankDataLoading, error: changePartnerBankDataError }] =
    useMutation(ADD_PARTNER_BANK_DATA)

  const [changePartnerContacts, { loading: changePartnerContactsLoading, error: changePartnerContactsError }] =
    useMutation(CHANGE_PARTNER_CONTACTS)

  const handleSubmit = async (formValues: INewPartnerStep2): Promise<void> => {
    const {
      createdEntities,
      deletedEntities: deletedContacts,
      editedEntities: editedContacts
    } = getEntitiesDifference(formValues.contacts, partner?.contacts ?? [EMPTY_PARTNER_CONTACT])

    const newBankData = getNewBankData(
      getDifference(formValues.bankData, partner?.bankData) as INewPartnerStep2['bankData']
    )
    const newContacts = getNewContacts(createdEntities)
    if (!isEmpty(editedContacts)) {
      await changePartnerContacts({
        variables: {
          partnerId: partnerId,
          input: {
            contacts: mapPartnerContactsToMutation(editedContacts) as GTChangePartnerContactInput[]
          }
        }
      })
    }

    if (!isEmpty(deletedContacts)) {
      await deletePartnerContacts({
        variables: {
          partnerId: partnerId,
          input: {
            contactIDs: deletedContacts.map((contact) => contact.id)
          }
        }
      })
    }

    if (!isEmpty(newContacts)) {
      await addPartnerContacts({
        variables: {
          partnerId: partnerId,
          input: {
            contacts: mapPartnerContactsToMutation(newContacts)
          }
        }
      })
    }

    if (!isEmpty(newBankData)) {
      await changePartnerBankData({
        variables: {
          partnerId: partnerId,
          input: mapPartnerBankDataToMutation(formValues.bankData)
        }
      })
    }

    onSubmit()
  }
  const isLoading =
    addPartnerContactsLoading ||
    changePartnerBankDataLoading ||
    changePartnerContactsLoading ||
    deletePartnerContactsLoading

  const submitError =
    addPartnerContactsError || changePartnerBankDataError || changePartnerContactsError || deletePartnerContactsError

  const initialValues: INewPartnerStep2 = {
    contacts: partner?.contacts || [],
    bankData: partner?.bankData || {
      bankName: '',
      clabeNumber: '',
      email: '',
      bankCoverLink: '',
      paymentType: ''
    }
  }

  return (
    <Formik
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validationSchema={schema(intl, {
        isEditContacts: partner?.contacts.some((contact) => contact.id),
        isEditBankData: !isEmpty(partner?.bankData.bankName || partner?.bankData.clabeNumber)
      })}
    >
      {({ handleSubmit, dirty }): ReactElement => {
        const isEditing = !isEmpty(partnerId) && dirty
        return (
          <FormLayout.Content as="form" onSubmit={handleSubmit}>
            {submitError && (
              <FormLayout.Actions>
                <ErrorMessage errors={getUnprocessableErrors(submitError)}>
                  <FormattedMessage
                    id="partners.new.submit.error"
                    defaultMessage="There was an error while adding the partner information"
                  />
                </ErrorMessage>
              </FormLayout.Actions>
            )}
            <PartnerDataContactsCard />
            <PartnerBankDataCard />
            <FormLayout.Actions>
              <Button type="button" kind="cancel" onClick={onCancel} disabled={isLoading}>
                <FormattedMessage id="dictionary.back" defaultMessage="Back" />
              </Button>
              <Button type="submit" kind="primary" loading={isLoading}>
                {isEditing ? (
                  <FormattedMessage id="partners.new.step2.saveAndContinue" defaultMessage="Save & Continue" />
                ) : (
                  <FormattedMessage id="dictionary.next" defaultMessage="Continue" />
                )}
              </Button>
            </FormLayout.Actions>
          </FormLayout.Content>
        )
      }}
    </Formik>
  )
}
